import { useEntityModalContext } from '@/contexts/EntityModalContext';
import { cn } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { EntityLink } from '@/components/ui/entity-link';
import { ParkingSessionStatusBadge } from '@/pages/parking-sessions/status-badge';
import type { ParkingSessionDisplayStatus } from '@/pages/parking-sessions/types';
import {
	formatCurrency,
	formatDateTime,
	formatDuration
} from '@/pages/parking-sessions/utils';

interface ParkingSessionDisplayItemProps {
	parkingSessionId: string;
	vehiclePlateNumber?: string | null;
	ticketId?: string | null;
	entryDateTime?: string | null;
	exitDateTime?: string | null;
	durationMinutes?: number | null;
	paymentAmount?: number | null;
	displayStatus?: ParkingSessionDisplayStatus;
	customerName?: string | null;
	parkingPositionCode?: string | null;
	className?: string;
}

const compact = (value?: string | null) => {
	const normalized = value?.trim();
	return normalized ? normalized : null;
};

export function ParkingSessionDisplayItem({
	parkingSessionId,
	vehiclePlateNumber,
	ticketId,
	entryDateTime,
	exitDateTime,
	durationMinutes,
	paymentAmount,
	displayStatus,
	customerName,
	parkingPositionCode,
	className
}: ParkingSessionDisplayItemProps) {
	const { openEntityModal } = useEntityModalContext();

	const displayTitle =
		[compact(vehiclePlateNumber), compact(ticketId)]
			.filter((value): value is string => Boolean(value))
			.join(' - ') || parkingSessionId;

	const entryLabel = formatDateTime(entryDateTime) ?? '-';
	const durationLabel = formatDuration(durationMinutes ?? undefined);
	const exitLabel = formatDateTime(exitDateTime);
	const amountLabel = formatCurrency(paymentAmount ?? undefined);

	const secondaryParts = [
		entryLabel,
		durationLabel,
		exitLabel ? `Exit ${exitLabel}` : null,
		amountLabel
	].filter((value): value is string => Boolean(value));

	const tertiaryParts = [
		compact(customerName),
		compact(parkingPositionCode)
			? `Pos ${compact(parkingPositionCode)}`
			: null
	].filter((value): value is string => Boolean(value));

	return (
		<div className={cn('bg-transparent', className)}>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="min-w-0">
							<EntityLink
								entityId={parkingSessionId}
								entityType="parkingSessions"
								displayText={displayTitle}
								onEntityClick={openEntityModal}
								className="truncate text-sm font-semibold text-primary"
							/>
						</div>
						<div className="shrink-0">
							<ParkingSessionStatusBadge status={displayStatus} />
						</div>
					</div>
					{secondaryParts.length > 0 && (
						<p className="mt-1 text-sm text-muted-foreground">
							{secondaryParts.join(' - ')}
						</p>
					)}
					{tertiaryParts.length > 0 && (
						<div className="mt-1 flex flex-wrap items-center gap-2">
							{tertiaryParts.map(part => (
								<Badge key={part} variant="outline">
									{part}
								</Badge>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
